'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { analytics } from '@/lib/analytics/posthog'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { intercomVisitor, useIntercom } from '@/components/integrations/intercom/intercom-provider'
import {
  IntegrationUnavailableDialog,
  type IntegrationUnavailableCopy,
} from '@/components/integrations/shared/integration-unavailable-dialog'
import { loadExternalScript } from '@/components/integrations/shared/load-script'
import { calendlyBooking } from '@/config/analytics'
import { attributionForManager, utmParameters } from '@/lib/analytics/attribution'
import { identifyByEmail, normalizeEmail, splitFullName } from '@/lib/analytics/identity'
import { firePixelConversion } from '@/lib/analytics/pixels'

type CalendlyContextValue = {
  open: (options?: {
    url?: string
    user?: string | null
    prefill?: { name?: string; email?: string }
    /** Stable slug of the entry point that opened the planner, carried onto `demo_opened`. */
    location?: string
  }) => void
  /** Preload the widget on hover/focus intent so `open` is instant without mount-time cost. */
  warmup: () => void
}

const CalendlyContext = createContext<CalendlyContextValue | null>(null)

const calendlyCssId = 'calendly-widget-css'
const calendlyJsId = 'calendly-widget-js'
/** How long a click waits for the widget before it says so; the next click downloads afresh. */
const calendlyLoadTimeoutMs = 10_000

/**
 * The booking page as we want it embedded.
 *
 * `hide_gdpr_banner` is the one that matters: without it Calendly puts its own cookie banner over
 * the calendar for anyone without their cookie — every first-time visitor — asking a second consent
 * question inside a dialog the visitor opened to pick a date. The colours make the frame agree with
 * the page it opens on.
 *
 * The stored UTMs ride along because Calendly keeps them on the invitee (`tracking.utm_*`) and sends
 * them in the `invitee.created` webhook — which is where the Manager creates `demo_request_received`,
 * and which usually beats the browser's own call to it. Only data inside the webhook is reliably
 * there when that conversion is created. A UTM the passed URL already carries is its own and stays.
 *
 * `salesforce_uuid` carries the booted Messenger's Intercom visitor id, and only when the Messenger
 * has booted. Nothing here is Salesforce: Calendly keeps this field on the invitee and passes it
 * through unchanged to `invitee.created` (`tracking.salesforce_uuid`), where the Manager reads it to
 * bind the booked address to this visitor — the binding the forms ask for with `intercomVisitorId`.
 * That pass-through is the only reason Calendly's field name is used. One the passed URL already
 * carries is its own and stays, like a UTM.
 */
function embedUrl(url: string, visitorId: string | undefined) {
  const embed = new URL(url)
  for (const [param, value] of Object.entries(utmParameters())) {
    if (value && !embed.searchParams.has(param)) {
      embed.searchParams.set(param, value)
    }
  }
  if (visitorId && !embed.searchParams.has('salesforce_uuid')) {
    embed.searchParams.set('salesforce_uuid', visitorId)
  }
  embed.searchParams.set('hide_gdpr_banner', '1')
  // `--color-accent-strong` rather than the accent itself: Calendly picks the label colour on a
  // primary-coloured button from its luminance, and the lighter accent lands just on the side where
  // it writes black on blue. The strong variant is the same blue, one step down, and gets white.
  embed.searchParams.set('primary_color', '1f6fe0')
  return embed.toString()
}

/**
 * The one origin a Calendly booking message is taken from: the scheduling page's own. widget.js
 * addresses its own messages to the frame with exactly this (`new URL(url).origin`), and the
 * planner URL is always on it. A substring test is not an origin check —
 * `https://calendly.com.attacker.invalid` contains `calendly.com`.
 */
const CALENDLY_ORIGIN = 'https://calendly.com'

/**
 * The invitee and event as Calendly's API names them, which is what `calendly.event_scheduled`
 * carries. The invitee pattern is the Manager's own (`App\Support\Calendly\InviteeUri::PATTERN`),
 * so the browser refuses exactly what the Manager would refuse instead of sending it on. Calendly's
 * embed docs print the invitee as `https://api.calendly.com/api/v2/scheduled_events/…`, but the API
 * reference serves the resource at `/scheduled_events/{event_uuid}/invitees/{invitee_uuid}` on
 * `https://api.calendly.com`, and that is the form both sides accept.
 */
const INVITEE_URI =
  /^https:\/\/api\.calendly\.com\/scheduled_events\/[A-Za-z0-9_-]{1,64}\/invitees\/[A-Za-z0-9_-]{1,64}$/
const EVENT_URI = /^https:\/\/api\.calendly\.com\/scheduled_events\/[A-Za-z0-9_-]{1,64}$/

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

/**
 * Reads a `calendly.event_scheduled` message, or refuses it.
 *
 * Returns the booking, or `null` for anything that is not a well-formed one: another Calendly event,
 * a payload that is not an object, a `uri` that is present but not shaped like Calendly's — or no
 * invitee at all. Calendly always names the invitee, and without one the browser has nothing to
 * hand the Manager and no dedup id to fire the pixel with. Refusing it loses nothing: the Calendly
 * `invitee.created` webhook records the booking server-side (`demo_request_received`) either way,
 * whereas a browser pixel without an id would count that booking a second time at the platforms.
 */
function parseBooking(data: unknown): { inviteeUri: string } | null {
  if (!isRecord(data) || data.event !== 'calendly.event_scheduled') {
    return null
  }
  if (!isRecord(data.payload)) {
    return null
  }

  const uriOf = (part: unknown, pattern: RegExp): string | undefined | null => {
    if (part === undefined) {
      return undefined
    }
    if (!isRecord(part)) {
      return null
    }
    if (part.uri === undefined) {
      return undefined
    }
    return typeof part.uri === 'string' && pattern.test(part.uri) ? part.uri : null
  }

  const inviteeUri = uriOf(data.payload.invitee, INVITEE_URI)
  const eventUri = uriOf(data.payload.event, EVENT_URI)
  if (!inviteeUri || eventUri === null) {
    return null
  }
  return { inviteeUri }
}

export function CalendlyProvider({
  url,
  unavailableCopy,
  children,
}: {
  url: string
  unavailableCopy: IntegrationUnavailableCopy
  children: ReactNode
}) {
  const [loadFailed, setLoadFailed] = useState(false)
  const requestMetaRef = useRef<{ user?: string | null; name?: string; email?: string } | null>(null)
  /**
   * The planner frame `open()` put on the page — the only window whose booking message is believed.
   * Any page holding a reference to this one (an opener, a popup, a frame of its own) can post it a
   * message with any data; only the frame widget.js created here was handed the planner URL.
   */
  const plannerFrameRef = useRef<HTMLIFrameElement | null>(null)
  const { setLauncherVisible, visitorIdAfterBoot } = useIntercom()

  const ensureCalendlyLoaded = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.resolve()
    }

    if (window.Calendly?.initPopupWidget) {
      return Promise.resolve()
    }

    if (!document.getElementById(calendlyCssId)) {
      const link = document.createElement('link')
      link.id = calendlyCssId
      link.rel = 'stylesheet'
      link.href = 'https://assets.calendly.com/assets/external/widget.css'
      document.head.appendChild(link)
    }

    return loadExternalScript({
      id: calendlyJsId,
      src: 'https://assets.calendly.com/assets/external/widget.js',
      timeoutMs: calendlyLoadTimeoutMs,
    }).then(() => {
      if (!window.Calendly?.initPopupWidget) {
        throw new Error('Calendly widget did not initialize')
      }
    })
  }, [])

  const open = useCallback(
    (options?: {
      url?: string
      user?: string | null
      prefill?: { name?: string; email?: string }
      location?: string
    }) => {
      requestMetaRef.current = {
        user: options?.user,
        name: options?.prefill?.name,
        email: options?.prefill?.email,
      }

      // Top of the demo funnel: the widget was opened (may or may not lead to a booking).
      // `location` is the entry point that opened it (nav, a page CTA, footer, …); undefined for
      // callers that don't pass one, which keeps every existing `demo_opened` caller unaffected.
      analytics.capture('demo_opened', { prefilled: Boolean(options?.prefill?.email), location: options?.location })

      // Staging books nothing. The planner is a real calendar belonging to a real person, so a
      // test environment that opens it fills that calendar with appointments someone has to cancel.
      // What staging does need to exercise is everything *after* the booking, so this runs the same
      // reporting path the real `calendly.event_scheduled` message runs, against a marked synthetic
      // invitee. It used to run a shortened version of its own that skipped identify and skipped the
      // pixel conversion entirely — so the one thing simulated mode existed to prove was the one
      // thing it never exercised.
      //
      // There is no webhook behind a simulated booking either, so the visitor id the real planner
      // hands Calendly as `salesforce_uuid` travels with the Manager call instead — together with
      // the prefilled address as `user`: the Manager's simulated branch reads `user` as the invitee's
      // email (a real booking's `user` is the campaign's external id, which it resolves through
      // Calendly instead) and binds the visitor only to an address it was given. Without a prefill
      // there is no address, and the Manager books its synthetic test invitee.
      //
      // The visitor id is read as late as it can be, and the Messenger is booted for it when it has
      // not booted yet. The forms never boot for the id, but a planner is different: a link that
      // opens it on arrival (`?demo=1`, `?action=request-demo`) acts before the deferred boot, and
      // measured on offsoo.dev on 15 October 2026 its booking reached the Manager without the id —
      // a lead beside the person, never merged. So the planner waits for the boot, bounded; a click
      // on "Plan een demo" before the boot takes the same path, and waits the same few seconds at
      // most. Without an id inside the bound the planner opens anyway: a booking is worth more than
      // the binding.
      const openWith = (visitorId: string | undefined) => {
        if (calendlyBooking === 'simulated') {
          void reportConversion(
            SIMULATED_INVITEE_URI,
            { user: options?.prefill?.email, email: options?.prefill?.email, name: options?.prefill?.name },
            { simulated: true, intercomVisitorId: visitorId },
          )
          return
        }

        if (window.Calendly?.initPopupWidget) {
          openPopup(visitorId)
          return
        }

        // The visitor asked for the planner, so a failure is theirs to know about (a warmup's is not).
        void ensureCalendlyLoaded()
          .then(() => openPopup(visitorId))
          .catch(() => setLoadFailed(true))
      }

      const openPopup = (visitorId: string | undefined) => {
        if (!window.Calendly?.initPopupWidget) {
          return
        }
        window.Calendly.initPopupWidget({
          url: embedUrl(options?.url ?? url, visitorId),
          prefill: options?.prefill,
        })

        // widget.js builds the popup synchronously: `.calendly-overlay` holding the planner's
        // iframe, appended to the body before `initPopupWidget` returns. Opening it again replaces
        // the overlay, and with it this frame; a closed planner's frame is detached and has no
        // `contentWindow` left to match.
        const overlay = document.querySelector('.calendly-overlay')
        plannerFrameRef.current = overlay?.querySelector('iframe') ?? null

        // Intercom's launcher is drawn above everything, so on a phone it sits on the calendar.
        // It steps aside for as long as the planner is open, and comes back when it closes.
        setLauncherVisible(false)
        if (overlay) {
          const observer = new MutationObserver(() => {
            if (!document.body.contains(overlay)) {
              observer.disconnect()
              setLauncherVisible(true)
            }
          })
          observer.observe(document.body, { childList: true })
        } else {
          setLauncherVisible(true)
        }
      }

      // The widget downloads while the Messenger boots, so the wait for the one is not added to the
      // other; the planner opens as soon as both are there.
      if (calendlyBooking !== 'simulated') {
        void ensureCalendlyLoaded().catch(() => undefined)
      }
      void visitorIdAfterBoot().then(openWith)
    },
    [ensureCalendlyLoaded, setLauncherVisible, url, visitorIdAfterBoot],
  )

  const warmup = useCallback(() => {
    void ensureCalendlyLoaded().catch(() => undefined)
  }, [ensureCalendlyLoaded])

  const value = useMemo(() => ({ open, warmup }), [open, warmup])

  useEffect(() => {
    // A booking message tells the Manager about a demo and reports a conversion to every ad
    // platform, so it is believed only when all three hold: it comes from Calendly's origin, from
    // the planner frame this page opened, and it is shaped like a booking. `window` hears messages
    // from any window that holds a reference to it, and anything can claim to be Calendly in `data`.
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== CALENDLY_ORIGIN) {
        return
      }
      const plannerWindow = plannerFrameRef.current?.contentWindow
      if (!plannerWindow || event.source !== plannerWindow) {
        return
      }
      const booking = parseBooking(event.data)
      if (!booking) {
        return
      }

      void reportConversion(booking.inviteeUri, requestMetaRef.current ?? undefined)
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <CalendlyContext.Provider value={value}>
      {children}
      <IntegrationUnavailableDialog open={loadFailed} onClose={() => setLoadFailed(false)} copy={unavailableCopy} />
    </CalendlyContext.Provider>
  )
}

/**
 * The invitee handed to the Manager in simulated mode. Deliberately not a plausible Calendly URI:
 * whatever the Manager does with it, the record it leaves is obviously a test.
 */
const SIMULATED_INVITEE_URI = 'https://api.calendly.com/scheduled_events/SIMULATED/invitees/STAGING'

/**
 * Reports one booked demo: identifies the person (before the Manager call when the planner was
 * prefilled, after it otherwise), tells the Manager, records the funnel event and fires the browser
 * ad pixels.
 *
 * Both entry points run this — the real `calendly.event_scheduled` message and the simulated
 * booking staging uses — so there is one description of what a booked demo does rather than two
 * that can drift. `simulated` tags the PostHog event and `intercomVisitorId` stands in for the
 * webhook a simulated booking has none of; everything else is identical, which is the point.
 */
async function reportConversion(
  inviteeUri: string,
  metadata?: { user?: string | null; name?: string; email?: string },
  { simulated = false, intercomVisitorId }: { simulated?: boolean; intercomVisitorId?: string } = {},
): Promise<void> {
  // A prefilled planner — a campaign link that names the person — knows the email before the
  // Manager is told, so the browser identifies as early as it can (ADR-0010). For a simulated
  // booking the Manager captures `demo_request_received` inside the request below, so this keeps the
  // browser the first writer of the person's first touch. For a real booking the Calendly webhook
  // captures it, independently of this page, and may land first; identifying early is then the
  // browser's best effort, not a guaranteed order. Without a prefill the email only arrives with the
  // response, and there is no earlier truth to protect.
  if (metadata?.email) {
    identifyInvitee(metadata.email, metadata.name)
  }

  const report = await reportDemoBooked(inviteeUri, metadata?.user ?? undefined, intercomVisitorId)

  // The Manager was not told — the request failed, answered non-2xx or with something that is not
  // JSON — so there is no id to dedupe on and no `repeat` to respect. The Calendly webhook records
  // the booking server-side regardless, so a browser pixel now could only count it a second time.
  // The funnel step is still the visitor's, and says why it carries no `conversion_id`. No retry:
  // the webhook already counts the booking.
  if (report.status === 'failed') {
    analytics.capture('demo_scheduled', { report_failed: true, ...(simulated ? { simulated: true } : {}) })
    return
  }

  const { data } = report
  const prefilledEmail = metadata?.email ? normalizeEmail(metadata.email) : undefined
  const bookedEmail = data.email ? normalizeEmail(data.email) : undefined

  // Who booked is the Manager's answer, not the prefill: a visitor can change the name and address
  // inside the planner, and then the prefill names someone else. Whenever the answer names the
  // invitee, the pixel carries the answer's person whole — email, name, external id, event id — so
  // no platform is handed one person's address next to another person's id. The prefill stands in
  // only when the answer names nobody — a success without an email, which only an older Manager
  // sends; the current one answers `502` instead (Manager ADR-0015).
  const sameAsPrefill = Boolean(prefilledEmail && bookedEmail === prefilledEmail)
  const email = bookedEmail ? data.email : metadata?.email
  const name = bookedEmail ? (data.name ?? (sameAsPrefill ? metadata?.name : undefined)) : metadata?.name
  // The backend returns the shared dedup eventId (`lead.<sha256(email)>`).
  const eventId = data.eventId

  // A booked demo is a key conversion. Email/name are PII, so identify the person when we have
  // their email and keep the event free of PII. Without a prefill this is the first identify. With
  // one, the Manager's answer may still add a name the prefill did not carry — identifying early
  // must not mean identifying with less; a second `identify()` for the same distinct id sends no
  // second `$identify`, posthog-js turns it into a person-property write. A booking under another
  // address is not identified again: that would move this visitor's PostHog person, history and
  // first touch included, onto whoever the planner was finally filled in for.
  if (!prefilledEmail) {
    if (data.email) {
      identifyInvitee(data.email, data.name)
    }
  } else if (sameAsPrefill && bookedEmail && !metadata?.name && data.name) {
    identifyInvitee(bookedEmail, data.name)
  }

  analytics.capture('demo_scheduled', { conversion_id: eventId, ...(simulated ? { simulated: true } : {}) })

  // Fire the browser ad pixels, deduped against the server-side CAPI by eventId.
  firePixelConversion('demo', {
    eventId,
    // The Manager returns the person's uuid on this endpoint too; without it the demo pixel was
    // the only conversion still sending no identity.
    externalId: data.externalId,
    email,
    ...splitFullName(name),
    // A second booking within 90 days is the same demo (workspace ADR 0015).
    repeat: data.repeat === true,
  })
}

/**
 * `first_name`/`last_name`, the two the CDP destinations read — not a single `name`, which nothing
 * consumes and which the Manager never writes. Split on the first space so a tussenvoegsel stays
 * with the surname: "Jan van der Berg" is Jan, van der Berg.
 */
function identifyInvitee(email: string, name: string | undefined): void {
  const { firstName, lastName } = splitFullName(name)
  identifyByEmail(email, { first_name: firstName, last_name: lastName })
}

/**
 * What the Manager answers `POST /request-demo` with. The current Manager always sends every field
 * for a real booking; each is still optional here, as tolerance for an older answer.
 */
type DemoReport = { eventId?: string; externalId?: string; name?: string; email?: string; repeat?: boolean }

/**
 * Tells the Manager a demo was booked, and hands back the shared dedup event id.
 *
 * Two outcomes, kept apart on purpose. `reported` is any 2xx JSON object, including one without an
 * `eventId` — the Manager answered that way before 2026-10-23 when it could not resolve an email, and
 * the pixel then fires without a dedup key; it now answers `502` instead (Manager ADR-0015), so this
 * stays only as tolerance for an older answer. `failed` is a request that never got that far: a
 * network error, a non-2xx answer — an unreadable Calendly invitee included — or a body that is not a
 * JSON object (workspace `docs/contracts/conversions.md`).
 */
async function reportDemoBooked(
  inviteeUri: string,
  user: string | undefined,
  intercomVisitorId: string | undefined,
): Promise<{ status: 'reported'; data: DemoReport } | { status: 'failed' }> {
  try {
    const response = await fetch(`/api/request-demo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The campaign as well, so the lead learns it even if Calendly ever dropped its tracking
      // fields, and so the simulated staging booking has a request to read it from.
      body: JSON.stringify({
        ...attributionForManager(),
        ...intercomVisitor(intercomVisitorId),
        url: inviteeUri,
        user,
      }),
    })
    if (!response.ok) {
      return { status: 'failed' }
    }
    const body: unknown = await response.json()
    if (!isRecord(body)) {
      return { status: 'failed' }
    }
    const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
    return {
      status: 'reported',
      data: {
        eventId: text(body.eventId),
        externalId: text(body.externalId),
        name: text(body.name),
        email: text(body.email),
        repeat: body.repeat === true,
      },
    }
  } catch {
    return { status: 'failed' }
  }
}

export function useCalendly() {
  const context = useContext(CalendlyContext)
  if (!context) {
    throw new Error('useCalendly must be used within a CalendlyProvider')
  }
  return context
}
